import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Divider, Paper } from '@mui/material'
import Grid from '@mui/material/Grid'
import { Add, MailOutlined } from '@mui/icons-material'
import { useAppId } from 'src/app/AppProvider'
import { useTranslate } from 'src/i18n/useMessageSource'
import { NavigateState } from 'src/routing/Routing'
import { Permission } from 'src/service/backend/api'
import { getEnumFromString } from 'src/service/utils/CommonUtils'
import { ListingButton } from 'src/ui-shared/base/button/ListingButton'
import { Hidden } from 'src/ui-shared/base/hidden/Hidden'
import { StyledTab } from 'src/ui-shared/base/tab-panel/StyledTab'
import { StyledTabs } from 'src/ui-shared/base/tab-panel/StyledTabs'
import { TabPanel } from 'src/ui-shared/base/tab-panel/TabPanel'
import { useSharedStyles } from 'src/ui-shared/constants/Shared.style'
import { ArticleIcon } from 'src/ui-shared/icons/Icons'
import { ScreenLayout } from 'src/ui/layout/main-layout/ScreenLayout'
import { MemberInvitationTable } from 'src/ui/page/common/member/list/MemberInvitationTable'
import { MemberTab } from 'src/ui/page/common/member/list/MemberTab'
import { MemberTable } from 'src/ui/page/common/member/list/MemberTable'
import { MemberInviteModal } from 'src/ui/page/common/member/list/invite/MemberInviteModal'
import { hasPermission } from 'src/user/RoleCheck'
import { useUser } from 'src/user/UserContext'

export const MemberListPage = (): ReactElement => {
  const translate = useTranslate()
  const { classes: sharedClasses } = useSharedStyles()

  const user = useUser()
  const appId = useAppId()
  const navigate = useNavigate()
  const { tabNameParam } = useParams()
  const tabName = tabNameParam ? getEnumFromString(tabNameParam, MemberTab) : MemberTab.OVERVIEW

  const [openInviteModal, setOpenInviteModal] = useState<boolean>(false)
  const [submittedInvite, setSubmittedInvite] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<MemberTab>(MemberTab.OVERVIEW)

  const hasInvitationReadPermission = hasPermission(user, Permission.INVITATION_READ)
  const hasInvitationWritePermission = hasPermission(user, Permission.INVITATION_WRITE)

  useEffect(() => {
    setActiveTab(tabName)
  }, [])

  const handleRowClick = (memberId?: string) => {
    navigate(`/${appId}/members/${memberId}/view`, {
      state: { browserHistoryBack: true } as NavigateState,
    })
  }

  const handleCreateButtonClick = () => {
    setOpenInviteModal(true)
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: MemberTab) => {
    const pathToNavigate = `/${appId}/members/${newValue}`
    navigate(pathToNavigate, { replace: true })
    setActiveTab(newValue)
  }

  const inviteAction = (
    <ListingButton onClick={handleCreateButtonClick} variant="outlined" color="primary" startIcon={<Add />}>
      {translate('invite')}
    </ListingButton>
  )

  const actions = hasInvitationWritePermission ? inviteAction : <></>

  return (
    <>
      <ScreenLayout title={translate('members')} actions={actions}>
        <Paper elevation={0}>
          <Grid container className={sharedClasses.TabsContainer}>
            <Grid size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <StyledTabs
                value={activeTab}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
                onChange={handleTabChange}
              >
                <StyledTab
                  icon={<ArticleIcon />}
                  iconPosition={'start'}
                  value={MemberTab.OVERVIEW}
                  label={<Hidden down="md">{translate('members')}</Hidden>}
                />
                {hasInvitationReadPermission && (
                  <StyledTab
                    icon={<MailOutlined />}
                    iconPosition={'start'}
                    value={MemberTab.INVITATIONS}
                    label={<Hidden down="md">{translate('invitiations')}</Hidden>}
                  />
                )}
              </StyledTabs>
            </Grid>
          </Grid>
          <Divider />
          <TabPanel value={activeTab} index={MemberTab.OVERVIEW}>
            <MemberTable navigateToItem={handleRowClick} />
          </TabPanel>
          {hasInvitationReadPermission && (
            <TabPanel value={activeTab} index={MemberTab.INVITATIONS}>
              <MemberInvitationTable submittedInvite={submittedInvite} />
            </TabPanel>
          )}
        </Paper>
      </ScreenLayout>

      <MemberInviteModal
        key={openInviteModal.toString()}
        openInviteModal={openInviteModal}
        setOpenInviteModal={setOpenInviteModal}
        setSubmittedInvite={setSubmittedInvite}
      />
    </>
  )
}
